/**
 * DeleteAccountSection Component
 * Allows users to permanently delete their account with confirmation.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/features/ui/components/Button';
import { deleteAccount } from '@/features/account/api/account.api';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { logger } from '@/features/help/utils/logger';

/**
 * Confirmation modal for account deletion
 */
interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isProcessing: boolean;
  confirmText: string;
  setConfirmText: (text: string) => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing,
  confirmText,
  setConfirmText,
}) => {
  if (!isOpen) return null;

  const isConfirmValid = confirmText === 'DELETE';

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out"
      aria-labelledby="delete-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 mx-4 w-full max-w-md relative transform transition-all duration-300 ease-in-out scale-100">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          aria-label="Close modal"
          disabled={isProcessing}
        >
          <X size={20} />
        </button>

        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <div className="mt-0 text-left flex-1">
            <h3 className="text-lg leading-6 font-medium text-gray-900" id="delete-modal-title">
              Final Confirmation
            </h3>
            <div className="mt-2 space-y-3">
              <p className="text-sm text-gray-600">
                This action is <span className="font-semibold text-red-600">permanent</span> and
                cannot be undone. All your data will be deleted immediately.
              </p>
              <div>
                <label htmlFor="confirm-delete-input" className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-mono bg-gray-100 px-1 rounded">DELETE</span> to confirm:
                </label>
                <input
                  id="confirm-delete-input"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  disabled={isProcessing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row-reverse sm:gap-3">
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!isConfirmValid || isProcessing}
            className="w-full sm:w-auto mb-2 sm:mb-0"
          >
            {isProcessing ? (
              <span className="flex items-center justify-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Deleting...
              </span>
            ) : (
              'Delete My Account'
            )}
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Main section component for account deletion in Settings
 */
export const DeleteAccountSection: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  /**
   * Opens the confirmation modal
   */
  const handleOpenModal = () => {
    logger.info('DeleteAccountSection', 'Opening delete account modal');
    setConfirmText('');
    setIsModalOpen(true);
  };

  /**
   * Closes the confirmation modal
   */
  const handleCloseModal = () => {
    logger.info('DeleteAccountSection', 'Closing delete account modal');
    setIsModalOpen(false);
    setConfirmText('');
  };

  /**
   * Executes the account deletion
   */
  const handleDeleteAccount = async () => {
    logger.info('DeleteAccountSection', 'User confirmed account deletion');
    setIsProcessing(true);

    try {
      await deleteAccount();

      logger.success('DeleteAccountSection', 'Account deleted successfully');

      // Sign out and redirect to login with confirmation message
      await logout();
      navigate('/login?accountDeleted=true', { replace: true });
    } catch (error) {
      logger.error('DeleteAccountSection', 'Failed to delete account', { error });
      toast.error('Failed to delete account. Please try again or contact support.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Delete Account</h2>

      <Button
        variant="destructive"
        onClick={handleOpenModal}
        className="flex items-center gap-2"
      >
        <Trash2 className="h-4 w-4" />
        Delete Account
      </Button>

      <DeleteConfirmModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleDeleteAccount}
        isProcessing={isProcessing}
        confirmText={confirmText}
        setConfirmText={setConfirmText}
      />
    </div>
  );
};

